// Integración con Zebra Browser Print (app local de Zebra): permite enviar
// ZPL crudo a la impresora DESDE el navegador, sin diálogo de impresión ni
// terminal. El servicio corre en el equipo del usuario y expone una API REST
// en loopback; la primera vez que el sitio lo contacta, Browser Print muestra
// un aviso para autorizar el origen (así se agrega demo.trazalogis.cl).
//
// Se usan los endpoints REST directamente (sin el SDK de Zebra):
//   GET  /default?type=printer  → impresora por defecto configurada en la app
//   GET  /available             → dispositivos conectados AHORA (uids vivos)
//   POST /write                 → { device, data } imprime el ZPL en crudo
// El POST va con Content-Type text/plain: es una "simple request" sin
// preflight CORS, que es exactamente como opera el SDK oficial.
//
// SELECCIÓN DE IMPRESORA (varias Zebra en el mismo equipo, p. ej. GK420t y
// TLP 2844-Z): el usuario elige en la app (ZebraPrinterSelect.vue) y la
// elección se guarda en localStorage — es una preferencia DEL EQUIPO (la
// impresora está enchufada a esta máquina), no del usuario. Sin elección se
// usa la impresora por defecto de Browser Print, como antes.
//
// GOTCHA (visto en la GK420t por USB): /default cachea el uid con la
// dirección USB de cuando se registró (…addr_005…); tras reiniciar el equipo
// o reenchufar, macOS asigna OTRA dirección y /write con el uid viejo da
// 500 "Unable to establish connection". /available sí trae el uid vivo, así
// que la impresora elegida/por defecto se reconcilia contra esa lista (el
// NÚMERO DE SERIE dentro del uid es lo que identifica al equipo físico).

import { profileForPrinter } from './zplLabel'

const BP_HOSTS = ['http://127.0.0.1:9100', 'https://127.0.0.1:9101']
const STORAGE_KEY = 'trazalogis.zebraPrinter'

const fetchTimeout = (url, options = {}, ms = 2500) => {
  const ctrl = new AbortController()
  const timer = setTimeout(() => ctrl.abort(), ms)
  return fetch(url, { ...options, signal: ctrl.signal }).finally(() => clearTimeout(timer))
}

/** Número de serie del dispositivo: va dentro del uid USB
 *  ("usb#vid_0a5f&pid_0028#45A054400319#bus_001#addr_006#model_…") y entre
 *  paréntesis en el nombre ("TLP2844-Z-200dpi (45A054400319)"). Las
 *  entradas de tipo "driver" (cola de impresión del sistema) no lo traen. */
export function printerSerial(device) {
  if (!device) return null
  const m = String(device.uid || '').match(/#([A-Za-z0-9]+)#bus_/)
  if (m) return m[1]
  const n = String(device.name || '').match(/\(([A-Za-z0-9]+)\)\s*$/)
  return n ? n[1] : null
}

/** Impresora elegida en este equipo (o null = usar la por defecto). */
export function getSavedPrinter() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : null
  } catch (e) { return null }
}

/** Guarda la impresora elegida (null = volver a la por defecto). */
export function savePrinter(device) {
  try {
    if (!device) localStorage.removeItem(STORAGE_KEY)
    else localStorage.setItem(STORAGE_KEY, JSON.stringify(device))
  } catch (e) { /* almacenamiento bloqueado: la elección solo dura la sesión */ }
}

/** Devuelve la entrada VIVA de /available que corresponde al device dado.
 *  Prioridad: mismo uid > mismo número de serie > mismo nombre y conexión >
 *  mismo nombre > (solo si allowSingle) el único printer disponible. */
function matchLive(printers, device, allowSingle = false) {
  if (!device || !printers.length) return null
  const serial = printerSerial(device)
  return printers.find(p => p.uid === device.uid) ||
    (serial && printers.find(p => printerSerial(p) === serial)) ||
    printers.find(p => p.name === device.name && p.connection === device.connection) ||
    printers.find(p => p.name === device.name) ||
    (allowSingle && printers.length === 1 ? printers[0] : null)
}

async function fetchAvailable(host) {
  try {
    const res = await fetchTimeout(`${host}/available`, { method: 'GET' })
    if (res.ok) return (await res.json()).printer || []
  } catch (e) { /* sin lista: se usa el device tal cual */ }
  return []
}

async function fetchDefault(host) {
  try {
    const res = await fetchTimeout(`${host}/default?type=printer`, { method: 'GET' })
    if (!res.ok) return null
    const device = await res.json()
    return device && device.name ? device : null
  } catch (e) { return null }
}

/** Lista las impresoras que Browser Print ve AHORA, más su impresora por
 *  defecto. Devuelve { host, printers, defaultDevice } o null si el
 *  servicio no responde en ningún puerto. */
export async function listBrowserPrintPrinters() {
  for (const host of BP_HOSTS) {
    const defaultDevice = await fetchDefault(host)
    if (!defaultDevice) {
      // Sin /default no hay servicio autorizado en este host (o no hay
      // impresora configurada): probar el siguiente puerto
      continue
    }
    const printers = await fetchAvailable(host)
    return { host, printers, defaultDevice }
  }
  return null
}

/** Resuelve A QUÉ impresora se imprime y con qué perfil ZPL:
 *  la elegida en este equipo (reconciliada con /available) o, sin elección,
 *  la por defecto de Browser Print. Devuelve { host, device, profile, source }.
 *  Lanza Error con .code = 'no-service' | 'printer-missing'. */
export async function resolveBrowserPrintTarget() {
  const svc = await listBrowserPrintPrinters()
  if (!svc) {
    const err = new Error('Zebra Browser Print no responde')
    err.code = 'no-service'
    throw err
  }
  const saved = getSavedPrinter()
  if (saved) {
    const live = matchLive(svc.printers, saved, false)
    if (!live) {
      const err = new Error(`La impresora elegida "${saved.name}" no está conectada`)
      err.code = 'printer-missing'
      err.printer = saved
      throw err
    }
    return { host: svc.host, device: live, profile: profileForPrinter(live), source: 'saved' }
  }
  const device = matchLive(svc.printers, svc.defaultDevice, true) || svc.defaultDevice
  if (device.uid !== svc.defaultDevice.uid) {
    console.log('[BrowserPrint] uid de /default obsoleto, usando el vivo de /available:', device.uid)
  }
  return { host: svc.host, device, profile: profileForPrinter(device), source: 'default' }
}

/** Compatibilidad: { host, device } de la impresora efectiva, o null. */
export async function findBrowserPrint() {
  try {
    const t = await resolveBrowserPrintTarget()
    return { host: t.host, device: t.device }
  } catch (e) { return null }
}

/** Envía ZPL crudo a la impresora efectiva (o al target ya resuelto con
 *  resolveBrowserPrintTarget — así el ZPL se genera con el perfil correcto).
 *  Lanza Error con .code = 'no-service' | 'printer-missing' | 'write-failed'
 *  y .detail con el diagnóstico real (status + cuerpo, o el error de red). */
export async function printZplViaBrowserPrint(zpl, target = null) {
  const svc = target || await resolveBrowserPrintTarget()
  console.log('[BrowserPrint] host:', svc.host, '| device:', svc.device, '| perfil:', svc.profile && svc.profile.id, '| zpl bytes:', zpl.length)
  try {
    await writeOnce(svc.host, svc.device, zpl)
  } catch (e) {
    // Uid muerto pese a la reconciliación (impresora reenchufada a mitad de
    // sesión): refrescar desde /available y reintentar UNA vez.
    if (e.code === 'write-failed' && /establish connection/i.test(e.detail || '')) {
      const fresh = matchLive(await fetchAvailable(svc.host), svc.device, !getSavedPrinter())
      if (fresh && fresh.uid !== svc.device.uid) {
        console.log('[BrowserPrint] reintentando /write con uid refrescado')
        await writeOnce(svc.host, fresh, zpl)
        return fresh.name
      }
    }
    throw e
  }
  return svc.device.name
}

async function writeOnce(host, device, zpl) {
  let res
  try {
    res = await fetchTimeout(`${host}/write`, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body: JSON.stringify({ device, data: zpl })
    }, 8000)
  } catch (e) {
    // Error de RED en el POST (CORS/timeout/abort) — el GET sí funcionó
    const err = new Error(`fallo de red en /write: ${e.name === 'AbortError' ? 'timeout' : e.message}`)
    err.code = 'write-failed'
    err.detail = String(e)
    console.error('[BrowserPrint] POST /write network error:', e)
    throw err
  }
  const bodyText = await res.text().catch(() => '')
  console.log('[BrowserPrint] /write →', res.status, bodyText)
  if (!res.ok) {
    const err = new Error(`Browser Print respondió ${res.status}${bodyText ? ': ' + bodyText.slice(0, 120) : ''}`)
    err.code = 'write-failed'
    err.detail = bodyText
    throw err
  }
}
